import {z} from 'zod';

import {omneticApi} from '@dms/api/base';

import {
  CreateInterestApiArg,
  CreateInterestApiResponse,
  DeleteInterestApiArg,
  DeleteInterestApiResponse,
  GetInterestApiArg,
  GetInterestApiResponse,
  GetInterestCatalogueApiResponse,
  UpdateInterestApiArg,
  UpdateInterestApiResponse,
  ConvertForAllVehiclesApiArg,
  ConvertForAllVehiclesApiResponse,
  ConvertForBuyingVehicleApiArg,
  ConvertForBuyingVehicleApiResponse,
  ConvertForSellingVehicleApiArg,
  ConvertForSellingVehicleApiResponse,
  ChangeInterestBranchApiArg,
  ChangeInterestBranchApiResponse,
  ChangeInterestCustomerApiArg,
  ChangeInterestCustomerApiResponse,
  GetInterestStatisticsApiArg,
  GetInterestStatisticsApiResponse,
  CreateInterestPriceReportApiResponse,
  CreateInterestPriceReportApiArg,
  GetInterestPriceReportsApiResponse,
  GetInterestPriceReportsApiArg,
  CreateMinisaleApiResponse,
  CreateMinisaleApiArg,
  UpdateMinisaleApiResponse,
  UpdateMinisaleApiArg,
  DeleteMinisaleApiArg,
  DeleteMinisaleApiResponse,
  GetMinisaleApiArg,
  GetMinisaleApiResponse,
  CreateMinipurchaseApiResponse,
  CreateMinipurchaseApiArg,
  UpdateMinipurchaseApiResponse,
  UpdateMinipurchaseApiArg,
  GetMinipurchaseApiResponse,
  GetMinipurchaseApiArg,
  GetOfferedVehiclesListApiResponse,
  GetOfferedVehiclesListApiArg,
  UpdateInterestUtmApiResponse,
  UpdateInterestUtmApiArg,
  ChangeMinisaleVatDeductibleApiArg,
  ChangeMinisaleVatDeductibleApiResponse,
  ChangeMinisaleCostReasonApiArg,
  ChangeMinisaleCostReasonApiResponse,
  ChangeMinisaleExpectedCostsApiArg,
  ChangeMinisaleExpectedCostsApiResponse,
  ChangeMinisalePriceOfferedApiArg,
  ChangeMinisalePriceOfferedApiResponse,
  OpenInterestApiResponse,
  EndInterestApiResponse,
  EndInterestApiArg,
  OpenInterestApiArg,
  ChangeMinisaleReasonApiResponse,
  ChangeMinisaleReasonApiArg,
  ChangeMinisalePriceRequestedApiArg,
  ChangeMinisalePriceRequestedApiResponse,
  AddMinipurchaseOfferedVehicleApiArg,
  AddMinipurchaseOfferedVehicleApiResponse,
  RemoveMinipurchaseOfferedVehicleApiArg,
  RemoveMinipurchaseOfferedVehicleApiResponse,
  GetInterestV2ApiArg,
  GetInterestV2ApiResponse,
  AddMinipurchaseOfferedVehicleRequestBodySchema,
  InterestResponseBodyV2ReadSchema,
  GetInterestV2ApiArgSchema,
  AddInterestTestDriveApiResponse,
  AddInterestTestDriveApiArg,
  GetInterestTestDriveApiResponse,
  GetInterestTestDriveApiArg,
  RemoveInterestTestDriveApiResponse,
  RemoveInterestTestDriveApiArg,
  UpdateInterestTestDriveApiResponse,
  UpdateInterestTestDriveApiArg,
  PriceReportPreviewApiResponse,
  PriceReportPreviewApiArg,
} from './types';

export const interestApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    createInterest: build.mutation<CreateInterestApiResponse, CreateInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/interest`,
        method: 'POST',
        body: queryArg.createInterestRequestBody,
      }),
      invalidatesTags: ['InterestCount'],
    }),
    convertForAllVehicles: build.mutation<
      ConvertForAllVehiclesApiResponse,
      ConvertForAllVehiclesApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/convert-all`,
        method: 'POST',
        params: {validateMandatoryFields: queryArg.validateMandatoryFields},
      }),
      invalidatesTags: (result) => [{type: 'Interest', id: result?.id ?? undefined}],
    }),
    convertForBuyingVehicle: build.mutation<
      ConvertForBuyingVehicleApiResponse,
      ConvertForBuyingVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/convert-buying`,
        method: 'POST',
        params: {validateMandatoryFields: queryArg.validateMandatoryFields},
      }),
      invalidatesTags: (result) => [{type: 'Interest', id: result?.id ?? undefined}],
    }),
    convertForSellingVehicle: build.mutation<
      ConvertForSellingVehicleApiResponse,
      ConvertForSellingVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/convert-selling/${queryArg.vehicleId}`,
        method: 'POST',
      }),
      invalidatesTags: (result) => [{type: 'Interest', id: result?.id ?? undefined}],
    }),
    deleteInterest: build.mutation<DeleteInterestApiResponse, DeleteInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.id}`,
        method: 'DELETE',
      }),
    }),
    /**
     * @deprecated use getInterestV2 query
     */
    getInterest: build.query<GetInterestApiResponse, GetInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.id}`,
      }),
      providesTags: (_, __, queryArg) => [{type: 'Interest', id: queryArg.id}],
    }),
    getInterestV2: build.query<GetInterestV2ApiResponse, GetInterestV2ApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/interest/${queryArg.id}`,
      }),
      providesTags: (_, __, queryArg) => [{type: 'Interest', id: queryArg.id}],
      extraOptions: {
        requestSchema: GetInterestV2ApiArgSchema,
        responseSchema: InterestResponseBodyV2ReadSchema,
      },
    }),
    updateInterest: build.mutation<UpdateInterestApiResponse, UpdateInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.id}`,
        method: 'PATCH',
        body: queryArg.updateInterestRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'Interest', id: queryArg.id},
        {type: 'InterestActivity', id: queryArg.id},
        {type: 'BusinessCaseActivity'},
      ],
    }),
    interestCatalogue: build.query<GetInterestCatalogueApiResponse, void>({
      query: () => ({
        url: `/dms/v1/interest/catalogue`,
      }),
    }),
    getInterestStatistics: build.query<
      GetInterestStatisticsApiResponse,
      GetInterestStatisticsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/statistics/interest/`,
        params: {branchId: queryArg.branchId},
      }),
      providesTags: ['Interest'],
    }),
    createInterestPriceReport: build.mutation<
      CreateInterestPriceReportApiResponse,
      CreateInterestPriceReportApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/price-report`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: ['interestPriceReport'],
    }),
    getInterestPriceReports: build.query<
      GetInterestPriceReportsApiResponse,
      GetInterestPriceReportsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/price-report`,
      }),
      providesTags: ['interestPriceReport'],
    }),
    changeInterestBranch: build.mutation<
      ChangeInterestBranchApiResponse,
      ChangeInterestBranchApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/change-branch`,
        method: 'POST',
        body: queryArg.changeInterestBranchRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [{type: 'Interest', id: queryArg.interestId}],
    }),
    changeInterestCustomer: build.mutation<
      ChangeInterestCustomerApiResponse,
      ChangeInterestCustomerApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/change-customer`,
        method: 'POST',
        body: queryArg.changeInterestCustomerRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [{type: 'Interest', id: queryArg.interestId}],
    }),
    createMinipurchase: build.mutation<CreateMinipurchaseApiResponse, CreateMinipurchaseApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/minipurchase`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        'miniPurchase',
        {type: 'Interest', id: queryArg.body.interestId},
      ],
    }),
    updateMinipurchase: build.mutation<UpdateMinipurchaseApiResponse, UpdateMinipurchaseApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/minipurchase`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'miniPurchase', id: queryArg.body.minipurchaseId},
        {type: 'Interest', id: queryArg.interestId},
        {type: 'InterestPriceReportPreview', id: queryArg.interestId},
      ],
    }),
    getMinipurchase: build.query<GetMinipurchaseApiResponse, GetMinipurchaseApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/minipurchase/${queryArg.minipurchaseId}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'miniPurchase', id: queryArg.minipurchaseId},
      ],
    }),
    createMinisale: build.mutation<CreateMinisaleApiResponse, CreateMinisaleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/minisale`,
        method: 'POST',
        body: queryArg.createMinisaleRequestBody,
        params: {validateMandatoryFields: queryArg.validateMandatoryFields},
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        'miniSale',
        {type: 'Interest', id: queryArg.createMinisaleRequestBody.interestId},
        {type: 'InterestPriceReportPreview', id: queryArg.createMinisaleRequestBody.interestId},
      ],
    }),
    updateMinisale: build.mutation<UpdateMinisaleApiResponse, UpdateMinisaleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/minisale`,
        method: 'PUT',
        body: queryArg.updateMinisaleRequestBody,
        params: {validateMandatoryFields: queryArg.validateMandatoryFields},
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'miniSale', id: queryArg.updateMinisaleRequestBody.minisaleId},
        {type: 'Interest', id: queryArg.updateMinisaleRequestBody.interestId},
        {type: 'InterestPriceReportPreview', id: queryArg.updateMinisaleRequestBody.interestId},
      ],
    }),
    deleteMinisale: build.mutation<DeleteMinisaleApiResponse, DeleteMinisaleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/minisale/${queryArg.minisaleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'Interest', id: queryArg.interestId},
        {type: 'miniSale', id: queryArg.minisaleId},
      ],
    }),
    getMinisale: build.query<GetMinisaleApiResponse, GetMinisaleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/minisale/${queryArg.minisaleId}`,
      }),
      providesTags: (_result, __error, queryArg) => [{type: 'miniSale', id: queryArg.minisaleId}],
    }),
    getOfferedVehiclesList: build.query<
      GetOfferedVehiclesListApiResponse,
      GetOfferedVehiclesListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/offered-cars-list`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    updateInterestUtm: build.mutation<UpdateInterestUtmApiResponse, UpdateInterestUtmApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.id}/utm`,
        method: 'PUT',
        body: queryArg.updateInterestUtmRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [{type: 'Interest', id: queryArg.id}],
    }),
    endInterest: build.mutation<EndInterestApiResponse, EndInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.id}/end`,
        method: 'POST',
        body: queryArg.endInterestRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'Interest', id: queryArg.id},
        {type: 'InterestActivity', id: queryArg.id},
      ],
    }),
    openInterest: build.mutation<OpenInterestApiResponse, OpenInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.id}/open`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'Interest', id: queryArg.id},
        {type: 'InterestActivity', id: queryArg.id},
        'participation',
      ],
    }),
    changeMinisaleCostReason: build.mutation<
      ChangeMinisaleCostReasonApiResponse,
      ChangeMinisaleCostReasonApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/minisale/${queryArg.minisaleId}/change-cost-reason`,
        method: 'POST',
        body: queryArg.changeMinisaleCostReasonRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'miniSale', id: queryArg.minisaleId},
        {type: 'Interest', id: queryArg.interestId},
      ],
    }),
    changeMinisaleExpectedCosts: build.mutation<
      ChangeMinisaleExpectedCostsApiResponse,
      ChangeMinisaleExpectedCostsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/minisale/${queryArg.minisaleId}/change-expected-costs`,
        method: 'POST',
        body: queryArg.changeMinisaleExpectedCostsRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'miniSale', id: queryArg.minisaleId},
        {type: 'Interest', id: queryArg.interestId},
      ],
    }),
    changeMinisalePriceOffered: build.mutation<
      ChangeMinisalePriceOfferedApiResponse,
      ChangeMinisalePriceOfferedApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/minisale/${queryArg.minisaleId}/change-price-offered`,
        method: 'POST',
        body: queryArg.changeMinisalePriceOfferedRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'miniSale', id: queryArg.minisaleId},
        {type: 'Interest', id: queryArg.interestId},
      ],
    }),
    changeMinisalePriceRequested: build.mutation<
      ChangeMinisalePriceRequestedApiResponse,
      ChangeMinisalePriceRequestedApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/minisale/${queryArg.minisaleId}/change-price-requested`,
        method: 'POST',
        body: queryArg.changeMinisalePriceRequestedRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'miniSale', id: queryArg.minisaleId},
        {type: 'Interest', id: queryArg.interestId},
      ],
    }),
    changeMinisaleReason: build.mutation<
      ChangeMinisaleReasonApiResponse,
      ChangeMinisaleReasonApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/minisale/${queryArg.minisaleId}/change-reason`,
        method: 'POST',
        body: queryArg.changeMinisaleReasonRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'miniSale', id: queryArg.minisaleId},
        {type: 'Interest', id: queryArg.interestId},
      ],
    }),
    changeMinisaleVatDeductible: build.mutation<
      ChangeMinisaleVatDeductibleApiResponse,
      ChangeMinisaleVatDeductibleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/minisale/${queryArg.minisaleId}/change-vat-deductible`,
        method: 'POST',
        body: queryArg.changeMinisaleVatDeductibleRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'miniSale', id: queryArg.minisaleId},
        {type: 'Interest', id: queryArg.interestId},
      ],
    }),
    addMinipurchaseOfferedVehicle: build.mutation<
      AddMinipurchaseOfferedVehicleApiResponse,
      AddMinipurchaseOfferedVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/minipurchase/${queryArg.minipurchaseId}/offered-vehicle`,
        method: 'PUT',
        body: queryArg.addMinipurchaseOfferedVehicleRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'miniPurchase', id: queryArg.minipurchaseId},
        'Interest',
        'InterestPriceReportPreview',
      ],
      extraOptions: {
        requestSchema: AddMinipurchaseOfferedVehicleRequestBodySchema,
        responseSchema: z.void(),
      },
    }),
    removeMinipurchaseOfferedVehicle: build.mutation<
      RemoveMinipurchaseOfferedVehicleApiResponse,
      RemoveMinipurchaseOfferedVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/minipurchase/${queryArg.minipurchaseId}/offered-vehicle/${queryArg.vehicleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'miniPurchase', id: queryArg.minipurchaseId},
        'Interest',
        'InterestPriceReportPreview',
      ],
    }),
    addInterestTestDrive: build.mutation<
      AddInterestTestDriveApiResponse,
      AddInterestTestDriveApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/test-drive`,
        method: 'PUT',
        body: queryArg.interestTestDriveRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'Interest', id: queryArg.interestId},
        {type: 'TestDrive', id: queryArg.interestId},
      ],
    }),
    getInterestTestDrive: build.query<GetInterestTestDriveApiResponse, GetInterestTestDriveApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/test-drive/${queryArg.interestTestDriveId}`,
      }),
      providesTags: (_, __, queryArg) => [
        {type: 'Interest', id: queryArg.interestId},
        {type: 'TestDrive', id: `${queryArg.interestId}-${queryArg.interestTestDriveId}`},
      ],
    }),
    removeInterestTestDrive: build.mutation<
      RemoveInterestTestDriveApiResponse,
      RemoveInterestTestDriveApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/test-drive/${queryArg.interestTestDriveId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'Interest', id: queryArg.interestId},
        {type: 'TestDrive', id: `${queryArg.interestId}-${queryArg.interestTestDriveId}`},
      ],
    }),
    updateInterestTestDrive: build.mutation<
      UpdateInterestTestDriveApiResponse,
      UpdateInterestTestDriveApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/test-drive/${queryArg.interestTestDriveId}`,
        method: 'POST',
        body: queryArg.interestTestDriveRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [
        {type: 'Interest', id: queryArg.interestId},
        {type: 'TestDrive', id: `${queryArg.interestId}-${queryArg.interestTestDriveId}`},
      ],
    }),
    createInterestPriceReportPreview: build.query<
      PriceReportPreviewApiResponse,
      PriceReportPreviewApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/price-report-preview`,
        method: 'POST',
        body: queryArg.createVehiclePriceReportRequestBody,
      }),
      providesTags: (_, __, queryArg) => [
        {type: 'InterestPriceReportPreview', id: queryArg.interestId},
      ],
    }),
  }),
});

export const {
  useCreateInterestMutation,
  useDeleteInterestMutation,
  useUpdateInterestMutation,
  useInterestCatalogueQuery,
  /**
   * @deprecated use getInterestV2 query instead
   */
  useGetInterestQuery,
  useGetInterestV2Query,
  useConvertForAllVehiclesMutation,
  useConvertForBuyingVehicleMutation,
  useConvertForSellingVehicleMutation,
  useGetInterestStatisticsQuery,
  useCreateInterestPriceReportMutation,
  useGetInterestPriceReportsQuery,
  useChangeInterestBranchMutation,
  useChangeInterestCustomerMutation,
  useCreateMinisaleMutation,
  useUpdateMinisaleMutation,
  useDeleteMinisaleMutation,
  useGetMinisaleQuery,
  useCreateMinipurchaseMutation,
  useUpdateMinipurchaseMutation,
  useGetMinipurchaseQuery,
  useGetOfferedVehiclesListQuery,
  useLazyGetMinipurchaseQuery,
  useUpdateInterestUtmMutation,
  useEndInterestMutation,
  useOpenInterestMutation,
  useChangeMinisaleCostReasonMutation,
  useChangeMinisaleExpectedCostsMutation,
  useChangeMinisalePriceOfferedMutation,
  useChangeMinisalePriceRequestedMutation,
  useChangeMinisaleReasonMutation,
  useChangeMinisaleVatDeductibleMutation,
  useRemoveMinipurchaseOfferedVehicleMutation,
  useAddMinipurchaseOfferedVehicleMutation,
  useAddInterestTestDriveMutation,
  useGetInterestTestDriveQuery,
  useRemoveInterestTestDriveMutation,
  useUpdateInterestTestDriveMutation,
  useCreateInterestPriceReportPreviewQuery,
} = interestApi;
